import React, { createContext, useCallback, useContext, useMemo } from 'react';

import { evalLiquid, renderLiquid, renderOutputEscapedLiquid } from '@waypoint/templates';

type Props = {
  strictVariablesRendering: boolean;
  skipLiquid: boolean;
  variables: Record<string, unknown>;
  children: JSX.Element | JSX.Element[];
};

type TState = {
  skipLiquid: boolean;
  strictVariablesRendering: boolean;
  variables: Record<string, unknown>;
};
const Context = createContext<TState | null>(null);

export function RootWaypointRendererProvider({ strictVariablesRendering, skipLiquid, variables, children }: Props) {
  const value = useMemo(
    () => ({ strictVariablesRendering, skipLiquid, variables }),
    [strictVariablesRendering, skipLiquid, variables]
  );
  return <Context.Provider value={value}>{children}</Context.Provider>;
}

type TNestedProps = {
  value: unknown;
  valueName: string;

  index: number;
  indexName: string;
  children: JSX.Element;
};
export function NestedWaypointRendererProvider({ value, valueName, index, indexName, children }: TNestedProps) {
  const { skipLiquid, strictVariablesRendering, variables } = useContext(Context)!;
  const context = useMemo(
    () => ({
      skipLiquid,
      strictVariablesRendering,
      variables: { ...variables, [valueName]: value, [indexName]: index },
    }),
    [strictVariablesRendering, skipLiquid, variables, value, valueName, index, indexName]
  );
  return <Context.Provider value={context}>{children}</Context.Provider>;
}

export function useSkipLiquid() {
  return useContext(Context)!.skipLiquid;
}

export function useRenderLiquid(): (template: string) => string {
  const { strictVariablesRendering, skipLiquid, variables } = useContext(Context)!;
  return useCallback(
    (template: string) => renderLiquid({ template, variables, strictVariablesRendering, skipLiquid }),
    [variables, strictVariablesRendering, skipLiquid]
  );
}

export function useRenderLiquidTemplate({ template, skipLiquid }: { template: string; skipLiquid: boolean }) {
  const { strictVariablesRendering, variables } = useContext(Context)!;
  return useMemo(
    () => renderLiquid({ template, variables, strictVariablesRendering, skipLiquid }),
    [template, variables, strictVariablesRendering, skipLiquid]
  );
}

export function useRenderOutputEscapedString(template: string) {
  const { strictVariablesRendering, variables } = useContext(Context)!;
  return useMemo(
    () => renderOutputEscapedLiquid({ template, variables, strictVariablesRendering }),
    [template, variables, strictVariablesRendering]
  );
}

export function useEvalLiquid() {
  const { skipLiquid, variables } = useContext(Context)!;
  return useCallback(
    (template: string) => {
      if (skipLiquid) {
        return template;
      }
      return evalLiquid({ template, variables });
    },
    [skipLiquid, variables]
  );
}

export function useCurrentVariables() {
  const { variables } = useContext(Context)!;
  return variables;
}

export function useCurrentSkipLiquid() {
  const { skipLiquid } = useContext(Context)!;
  return skipLiquid;
}
